import { useMemo, useEffect, useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import {
  useLocationSearch,
  ISearchParams,
  MAX_PAGE_SIZE,
} from 'src/utilities/useSearchHooks';
import { DropdownOptionProps } from 'src/components/CookbookSelectWrapper/CookbookSelectWrapper';
import { useFetchLocation } from 'src/components/common/hooks/useFetchLocation/useFetchLocation';
import {
  combineLocations,
  transformToOptions,
} from 'src/components/common/LocationsSelectSearchControl/hooks/useLocationsSelectSearchControl.utils';

export type UseLocationsSelectSearchControlReturnType = {
  options: DropdownOptionProps[];
  handleSearchChange: (query?: string) => Promise<void>;
  isLoadingFetchedLocation: boolean;
};

export type UseLocationsSelectSearchControlProps = {
  name: string;
};

export const useLocationsSelectSearchControl = ({
  name,
}: UseLocationsSelectSearchControlProps): UseLocationsSelectSearchControlReturnType => {
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const { watch } = useFormContext();
  const locationId = watch(name);
  const [hasSearchQuery, setHasSearchQuery] = useState<boolean>(false);
  const { doSearch, resultsList, unsubscribeFromSearch } = useLocationSearch(
    corpId,
    MAX_PAGE_SIZE
  );
  const {
    location: fetchedLocation,
    fetchLocation,
    isLoading: isLoadingFetchedLocation,
  } = useFetchLocation(corpId);

  useEffect(() => {
    if (locationId) {
      fetchLocation(locationId);
    }
  }, [locationId, fetchLocation]);

  const transformedOptions = useMemo((): DropdownOptionProps[] => {
    const allLocations = combineLocations({
      resultsList,
      fetchedLocation,
      hasSearchQuery,
    });
    return transformToOptions({ locations: allLocations });
  }, [fetchedLocation, resultsList, hasSearchQuery]);

  const handleSearchChange = useCallback((query?: string): Promise<void> => {
    const trimmedQuery = query?.trim();
    setHasSearchQuery(Boolean(trimmedQuery));

    const searchParams: ISearchParams = { size: MAX_PAGE_SIZE };
    if (trimmedQuery) {
      searchParams.query = trimmedQuery;
    }

    return doSearch(searchParams);
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      unsubscribeFromSearch();
    };
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, []);

  return {
    options: transformedOptions,
    handleSearchChange,
    isLoadingFetchedLocation,
  };
};
